/**
 * SyncProvider — Mounts cross-tab (BroadcastChannel) and cross-user
 * (Supabase Realtime) query invalidation listeners.
 *
 * Place inside TenantProvider + QueryClientProvider.
 */

import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { useTenant } from '@/contexts/TenantContext'
import { supabase } from '@/lib/supabase'
import { createBroadcastListener } from '@/lib/sync/broadcast-channel'
import { createRealtimeSync } from '@/lib/sync/realtime-sync'

interface SyncProviderProps {
  children: ReactNode
}

export function SyncProvider({ children }: SyncProviderProps) {
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuth()
  const { currentOrganization } = useTenant()

  // Layer 1: BroadcastChannel — cross-tab sync for the same user
  useEffect(() => {
    if (!isAuthenticated) return
    return createBroadcastListener(queryClient)
  }, [isAuthenticated, queryClient])

  // Layer 2: Supabase Realtime — cross-user sync
  useEffect(() => {
    if (!currentOrganization?.id) return

    return createRealtimeSync({ supabase, queryClient })
  }, [currentOrganization?.id, queryClient])

  return <>{children}</>
}
